<template>
  <div class="box bg-white border border-gray-200 rounded-lg p-5">
    <div class="flex items-start justify-between gap-3 mb-5">
      <div>
        <h3 class="text-lg font-medium text-primary-1">{{ title }}</h3>
        <p class="text-xs text-gray-400 mt-0.5">{{ datasetLabel }} · {{ sectionLabel }}</p>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-1.5 text-sm rounded-md px-3 py-1.5 hover:bg-primary-1/40 bg-primary-1/20 flex-none"
        @click="$emit('remove')"
      >
        <Trash2Icon class="w-4 h-4" />
        ลบการแสดงข้อมูล
      </button>
    </div>

    <div class="grid gap-4 mb-6" :style="{ gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))` }">
      <StatCard
        v-for="(c, i) in categories"
        :key="c"
        :label="c"
        :value="statValues[i]"
        :unit="unit"
      />
    </div>

    <!-- by-province stacked chart -->
    <h4 class="text-sm font-medium text-primary-3 mb-3">ข้อมูลตามจังหวัด</h4>
    <BarChart
      :chart-id="chartId"
      type="bar"
      :data="provinceData"
      :options="chartOptions"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import StatCard from "./StatCard.vue";
const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data, barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const barOptions = ({ legend = false, stacked = false, horizontal = false, aspectRatio = 2 } = {}) => {
  const cat = { stacked, grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: horizontal ? 0 : 45, minRotation: horizontal ? 0 : 45 } };
  const val = { stacked, beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 10 } } };
  return {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    aspectRatio,
    plugins: {
      legend: { display: legend, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
      tooltip: { enabled: true },
    },
    scales: horizontal ? { x: val, y: cat } : { x: cat, y: val },
  };
};

const props = defineProps({
  id: { type: [Number, String], required: true },
  title: { type: String, default: "" },
  datasetLabel: { type: String, default: "" },
  sectionLabel: { type: String, default: "" },
  unit: { type: String, default: "ครัวเรือน" },
  categories: { type: Array, default: () => [] },
});
defineEmits(["remove"]);

const chartId = computed(() => `addedPanel-${props.id}`);

const COLORS = ["#3A506A", "#20A0D7", "#F4D35E", "#11D67A", "#F73C4B"];

const PROVINCES = [
  "กระบี่", "กาญจนบุรี", "กาฬสินธุ์", "กำแพงเพชร", "ขอนแก่น", "จันทบุรี", "ฉะเชิงเทรา", "ชลบุรี", "ชัยนาท", "ชัยภูมิ",
  "ชุมพร", "เชียงราย", "เชียงใหม่", "ตรัง", "ตราด", "ตาก", "นครนายก", "นครปฐม", "นครพนม", "นครราชสีมา",
  "นครศรีธรรมราช", "นครสวรรค์", "นนทบุรี", "นราธิวาส", "น่าน", "บึงกาฬ", "บุรีรัมย์", "ปทุมธานี", "ประจวบคีรีขันธ์", "ปราจีนบุรี",
  "ปัตตานี", "พระนครศรีอยุธยา", "พะเยา", "พังงา", "พัทลุง", "พิจิตร", "พิษณุโลก", "เพชรบุรี",
];

const BASE = [10000, 4000, 3000, 4000, 5000, 11000];
const segBase = (catIndex) => BASE[catIndex % BASE.length];

const provinceData = computed(() => ({
  labels: PROVINCES,
  datasets: props.categories.map((c, ci) =>
    bar(
      c,
      COLORS[ci % COLORS.length],
      PROVINCES.map((p, pi) => Math.max(500, segBase(ci) + (((pi * 7 + ci * 13) % 9) - 4) * 150)),
      { barPercentage: 0.8, categoryPercentage: 0.7 }
    )
  ),
}));

const chartOptions = barOptions({ legend: true, stacked: true, aspectRatio: 3.2 });

const statValues = computed(() => props.categories.map((c, i) => (i === 0 ? 12000000 : 50000000)));
</script>
